<script lang="ts" setup>
import { format, isAfter, isSameDay, parse, parseISO } from 'date-fns'
import { de } from 'date-fns/locale'
import type { ParsedToccoModuleFieldsFragment } from '~/types'

const props = defineProps<{
  entity: ParsedToccoModuleFieldsFragment
}>()

const { t } = useI18n()
const { addModule, removeModule } = useSeminarCartStore()

const fieldToccoData = computed(() => props.entity.fieldToccoData)

const addedToCart = ref(false)
const showClassDays = ref(false)
const formattedStartDate = computed(() =>
  props.entity.fieldStartDate?.value
    ? format(parse(props.entity.fieldStartDate.value, 'yyyy-MM-dd', new Date()), 'dd.MM.yyyy')
    : '',
)
const formattedEndDate = computed(() =>
  props.entity.fieldEndDate?.value
    ? format(parse(props.entity.fieldEndDate.value, 'yyyy-MM-dd', new Date()), 'dd.MM.yyyy')
    : '',
)
const showEndDate = computed(() => {
  const startValue = props.entity.fieldStartDate?.value
  const endValue = props.entity.fieldEndDate?.value
  return Boolean(endValue && (!startValue || !isSameDay(parseISO(startValue), parseISO(endValue))))
})

const isOnWaitingList = computed(
  () =>
    parseInt(String(fieldToccoData.value?.paths.registration?.value ?? '')) >=
    parseInt(String(fieldToccoData.value?.paths.participation_max?.value ?? '')),
)
const isLastPlaces = computed(
  () =>
    parseInt(String(fieldToccoData.value?.paths.participation_max?.value ?? '')) -
      parseInt(String(fieldToccoData.value?.paths.registration?.value ?? '')) <=
    3,
)
const registrationInfo = computed(() =>
  isOnWaitingList.value
    ? t('tocco.detail.sidebarRegistrationInfoWaitingList')
    : isLastPlaces.value
      ? t('tocco.detail.sidebarRegistrationInfoLastPlaces')
      : null,
)

const formatLocalized = (date: Date, formatString: string) => {
  return format(date, formatString, { locale: de })
}

//TODO: add type
const classDaysList = computed(() =>
  fieldToccoData.value?.paths.relReservation.value
    ? fieldToccoData.value?.paths.relReservation.value
        .map((nextDate) => parseISO(nextDate.paths?.date_from?.value ?? ''))
        .sort((firstDate: Date, secondDate: Date) => (isAfter(firstDate, secondDate) ? 1 : -1))
        .map((nextDate: Date) => formatLocalized(nextDate, 'EEEE, dd. LLLL yyyy'))
        .filter((value: string, index: number, self: string[]) => self.indexOf(value) === index)
    : [],
)

const classDaysFirstHalf = computed(() => {
  const middleIndex = Math.ceil(classDaysList.value.length / 2)

  return classDaysList.value.slice().splice(0, middleIndex)
})

const classDaysSecondHalf = computed(() => {
  const middleIndex = Math.ceil(classDaysList.value.length / 2)

  return classDaysList.value.slice().splice(middleIndex, classDaysList.value.length)
})

watch(addedToCart, (value) => {
  if (value) {
    addModule(props.entity)
  } else {
    removeModule(props.entity)
  }
})
</script>

<template>
  <div
    :class="[
      'item',
      {
        'no-location-and-label': fieldToccoData?.paths.city.value === '' && !registrationInfo,
      },
    ]"
  >
    <div class="item-first">
      <Checkbox
        id="module-add-to-cart-checkbox"
        v-model="addedToCart"
        :name="`add-to-cart-${entity.id}`"
        :required="false"
      />
      <h5 v-if="entity.fieldStartDate">
        {{ formattedStartDate }}
      </h5>

      <Icon v-if="showEndDate" name="textlink" />

      <h5 v-if="showEndDate">
        {{ formattedEndDate }}
      </h5>
    </div>
    <div v-if="fieldToccoData?.paths.city.value !== ''" class="item-second">
      <div class="location">
        <Icon name="pin" />
        <div class="item-city">
          {{ fieldToccoData.paths.city.value }}
        </div>
      </div>
    </div>
    <div class="item-third">
      <p
        v-if="registrationInfo"
        :class="{
          'is-red': isOnWaitingList,
          'is-green': isLastPlaces,
        }"
      >
        {{ registrationInfo }}
      </p>
    </div>
    <div class="item-fourth">
      <span class="days-label" @click="showClassDays = !showClassDays">
        {{ t('tocco.teaser.labels.classDays') }}
        <Icon v-if="showClassDays" name="mini-minus-small" />
        <Icon v-if="!showClassDays" name="mini-plus-small" />
      </span>
    </div>
  </div>
  <div v-show="showClassDays" class="item-details">
    <h5>{{ t('tocco.teaser.labels.classDays') }}</h5>
    <div class="class-days">
      <div class="class-days-column">
        <div v-for="classDay in classDaysFirstHalf" :key="classDay" class="class-days-item f-18">
          {{ classDay }}
        </div>
      </div>
      <div class="class-days-column">
        <div v-for="classDay in classDaysSecondHalf" :key="classDay" class="class-days-item f-18">
          {{ classDay }}
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.item {
  --padding-block: 30px;
  --padding-inline: 20px;

  position: relative;

  padding-block: var(--padding-block);
  padding-inline: var(--padding-inline);

  border-left: 1px solid var(--c-line-light);
  border-right: 1px solid var(--c-line-light);
  border-bottom: 1px solid var(--c-line-light);

  display: grid;
  grid-template-columns: 1fr;
  align-items: center;
  gap: 10px;

  transition: opacity 0.3s ease;

  &:hover {
    opacity: 1 !important;
  }

  @include breakpoint(ts) {
    grid-template-columns: repeat(2, auto);
    gap: 20px;

    .item-first {
      order: 1;
    }
    .item-second {
      order: 3;

      grid-column: span 2;
    }
    .item-third {
      order: 2;

      justify-self: flex-end;
    }

    .item-fourth {
      order: 4;

      grid-column: span 2;
      justify-self: flex-end;
    }
  }

  @include breakpoint(ds) {
    grid-template-columns: repeat(4, auto);

    padding-left: 48px;
    padding-right: 40px;

    .item-first {
      order: 1;
    }

    .item-second {
      order: 2;

      grid-column: span 1;
    }

    .item-third {
      order: 3;
    }

    .item-fourth {
      order: 4;

      grid-column: span 1;
    }
  }

  @include breakpoint(d) {
    grid-template-columns: 340px repeat(3, auto);
    padding-block: 38px;
  }

  .item-first {
    display: grid;
    align-items: center;
    grid-template-columns: auto auto 18px auto;
    gap: 5px;
    text-align: center;

    max-width: 340px;
    margin-bottom: 0;

    @include breakpoint(tl) {
      gap: 24px;
    }

    h5 {
      margin-bottom: 0;
      @include fluid(font-size, 16px, 20px);

      @include breakpoint(tl) {
        line-height: 26px;
      }
    }
  }

  .item-second {
    justify-self: flex-start;
    @include fluid(font-size, 14px, 16px);

    .location {
      --svg-size: 24px;

      display: grid;
      grid-template-columns: auto auto;
      align-items: center;
      gap: 14px;

      svg {
        width: var(--svg-size);
        height: var(--svg-size);
      }

      .item-city {
        color: var(--c-primary);
      }
    }
  }

  .item-third {
    p {
      font-weight: 700;

      display: inline-block;
      text-align: center;
      border-radius: 3px;
      padding: 0 8px;

      margin-bottom: 0;

      @include fluid(font-size, 14px, 16px);
      @include breakpoint(ds) {
        padding: 0 10px;
      }

      &.is-green {
        border: 1px solid var(--c-green-100);
        color: var(--c-green-100);
      }
      &.is-red {
        border: 1px solid var(--c-error);
        color: var(--c-error);
      }
    }
  }

  .item-fourth {
    margin-top: 15px;

    cursor: pointer;

    @include breakpoint(tl) {
      margin-top: 0;
    }

    .days-label {
      text-transform: uppercase;
      line-height: 16px;
      letter-spacing: 1.5px;
      font-size: 13px;
      font-weight: 700;

      color: var(--c-primary);

      display: flex;
      align-items: center;
      gap: 5px;
    }
  }
}

.item-details {
  background: var(--c-background-light);

  display: grid;
  gap: 20px;
  padding: 28px;

  @include breakpoint(ds) {
    padding: 48px;
    grid-template-columns: 310px auto;
    gap: 40px;
  }

  h5 {
    font-weight: 700;

    @include fluid(font-size, 16px, 20px);
    @include breakpoint(ds) {
      padding-left: 40px;
    }
  }

  .class-days {
    display: grid;
    grid-template-columns: auto;
    gap: 20px;

    @include breakpoint(tl) {
      grid-template-columns: 1fr 1fr;
    }

    @include breakpoint(ds) {
      gap: 40px;
    }

    .class-days-column {
      .class-days-item {
        border-bottom: 1px solid var(--c-line-light);
        color: var(--c-text-dark);

        padding-bottom: 16px;

        &:not(:first-child) {
          padding-top: 16px;
        }
      }
    }
  }
}
</style>
